import { useEffect, useRef } from 'react';

import scrollIntoView from 'smooth-scroll-into-view-if-needed';
import {
  useEditorTabStateActions,
  useEditorTabStateSelector,
} from 'src/editor/editor-tab-state-registry';
import { domElIdCreators, getScrollParent } from 'src/helpers';
import { useActiveCommentId } from 'src/hooks/comment';

import type { CommentPanelComment } from './CommentsDrawer';

/**
 * Keeps the comment the user is looking at visible in the comments panel: the active comment
 * whenever it changes, and the target of every margin marker click, even a repeated one.
 */
export function useScrollCommentBoxIntoView(renderedComments: CommentPanelComment[]): void {
  const activeCommentId = useActiveCommentId();
  const scrollRequest = useEditorTabStateSelector(state => state.comments.scrollRequest);
  const {
    comments: { clearScrollRequest },
  } = useEditorTabStateActions();
  const scrolledActiveCommentIdRef = useRef<UUID | null>(null);

  useEffect(() => {
    if (!activeCommentId) {
      scrolledActiveCommentIdRef.current = null;
      return;
    }
    if (scrolledActiveCommentIdRef.current === activeCommentId) return;
    if (!scrollCommentBoxIntoView(activeCommentId)) return;

    scrolledActiveCommentIdRef.current = activeCommentId;
  }, [activeCommentId, renderedComments]);

  useEffect(() => {
    if (!scrollRequest) return;

    /**
     * A request is handled by the list it arrives at. When that list hides the comment (the filter
     * excludes it, or it is no longer in the document) the request is dropped rather than queued,
     * so it cannot fire later when the filter changes. Requests made while this panel is not
     * mounted (thread or new-comment view) stay in the tab state until the list mounts.
     */
    const isRendered = renderedComments.some(comment => comment.nodeRefId === scrollRequest.target);
    if (isRendered) scrollCommentBoxIntoView(scrollRequest.target);
    clearScrollRequest(scrollRequest.requestId);
  }, [clearScrollRequest, renderedComments, scrollRequest]);
}

function scrollCommentBoxIntoView(commentId: UUID) {
  const commentBoxEl = document.getElementById(domElIdCreators.docCommentBoxEl(commentId));
  if (!commentBoxEl) return false;

  void scrollIntoView(commentBoxEl, {
    behavior: 'smooth',
    boundary: getScrollParent(commentBoxEl),
    scrollMode: 'if-needed',
  });
  return true;
}
